import { formatPhone, memberCountLabel, type AdminEarlyAccessRequest, type AdminSignUpAllowance } from "@teamna/shared";
import { Link } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import {
  Badge,
  Button,
  Callout,
  Card,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Field,
  PageHeader,
  SkeletonList,
  showToast,
} from "@teamna/ui/components/index.ts";
import { isApiError } from "@teamna/web-client/api/client.ts";
import { formatDate, formatDateTime } from "@teamna/web-client/lib/format.ts";
import { useAllowSignUp, useEarlyAccess, useTakeBackSignUp, type TakeBackTarget } from "../lib/cc-api.ts";
import { sportName } from "./format.ts";

/**
 * Early access (Anas, 2026-11-01): the website forms, and the allow list.
 *
 * While the switch "Owner sign-up" in Settings is off, nobody can create a business without an
 * invitation. A founder reads a form here (or in #new-clubs), presses "Allow sign-up", and sends
 * the person the message below on WhatsApp. The person signs in with the number (or the email) from
 * the form and creates the business. The row then shows "Signed up".
 */

/** The WhatsApp message for a person we let in. Plain words: it goes to a business owner. */
export const LET_IN_MESSAGE =
  "Good news: your Teamna early access is ready. Open https://teamna.app/sign-in and sign in with the mobile number or the email that you gave us. Then add your business and follow the setup steps.";

const whatsAppLink = (e164: string) => `https://wa.me/${e164.replace(/^\+/, "")}`;

function errorText(error: unknown, fallback: string): string {
  return isApiError(error) && error.detail ? error.detail : fallback;
}

function copyLetInMessage() {
  void navigator.clipboard?.writeText(LET_IN_MESSAGE).then(
    () => showToast("Message copied. Paste it in WhatsApp."),
    () => showToast("Could not copy. Select the text and copy it.", { tone: "info" }),
  );
}

function AllowanceStatus({ allowance }: { allowance: AdminSignUpAllowance }) {
  return allowance.usedAt ? (
    <Badge tone="confirmed">Signed up {formatDate(allowance.usedAt)}</Badge>
  ) : (
    <Badge tone="info">Allowed, not signed up yet</Badge>
  );
}

function TakeBackButton({ target, label }: { target: TakeBackTarget; label: string }) {
  const remove = useTakeBackSignUp();
  const [asking, setAsking] = useState(false);
  return (
    <>
      <Button variant="secondary" onClick={() => setAsking(true)} aria-label={`Take back sign-up, ${label}`}>
        Take back
      </Button>
      <ConfirmDialog
        open={asking}
        title={`Take back sign-up for ${label}?`}
        confirmLabel="Take back"
        tone="danger"
        loading={remove.isPending}
        onConfirm={() =>
          remove.mutate(target, {
            onSuccess: () => {
              setAsking(false);
              showToast(`${label} is off the early access list.`);
            },
            onError: (error) => showToast(errorText(error, "Could not take it back. Try again."), { tone: "info" }),
          })
        }
        onCancel={() => setAsking(false)}
      >
        This removes the person from the early access list. If they have no business, they cannot create one while early access is on. A business they made already stays, and its owner can still add another.
      </ConfirmDialog>
    </>
  );
}

function Detail({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-[12px] text-ink-3">{label}</dt>
      <dd className="text-[14px] text-ink [overflow-wrap:anywhere]">{children}</dd>
    </div>
  );
}

function RequestCard({ request }: { request: AdminEarlyAccessRequest }) {
  const allow = useAllowSignUp();
  const isPhone = request.contact.startsWith("+");
  const owner = request.role === "owner";

  const letIn = () =>
    allow.mutate(
      { waitlistSignupId: request.id },
      {
        onSuccess: () => showToast(`${request.businessName} can sign up now. Send them the message.`),
        onError: (error) => showToast(errorText(error, "Could not allow sign-up. Try again."), { tone: "info" }),
      },
    );

  return (
    <Card>
      <div className="flex flex-col gap-3 border-b border-line px-4 py-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-[17px] font-semibold text-ink">{request.businessName}</h2>
            <Badge>{sportName(request.businessType)}</Badge>
            {owner ? null : <Badge>Parent or member</Badge>}
            {request.allowance ? <AllowanceStatus allowance={request.allowance} /> : null}
          </div>
          <p className="mt-1 text-[13px] text-ink-3">
            {request.contactName}. Sent {formatDateTime(request.createdAt)}.
            {request.utmSource ? ` From ${request.utmSource}${request.utmCampaign ? ` (${request.utmCampaign})` : ""}.` : ""}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {request.allowance ? (
            <>
              {request.allowance.usedAt ? null : (
                <Button variant="secondary" onClick={copyLetInMessage}>
                  Copy WhatsApp message
                </Button>
              )}
              <TakeBackButton target={{ requestId: request.id }} label={request.businessName} />
            </>
          ) : owner ? (
            <Button onClick={letIn} loading={allow.isPending} aria-label={`Allow sign-up, ${request.businessName}`}>
              Allow sign-up
            </Button>
          ) : null}
        </div>
      </div>
      <dl className="grid gap-3 px-4 py-3 sm:grid-cols-3">
        <Detail label="Mobile number">
          {isPhone ? (
            <a className="text-accent-strong underline" href={whatsAppLink(request.contact)} target="_blank" rel="noreferrer">
              {formatPhone(request.contact)}
            </a>
          ) : (
            request.contact
          )}
        </Detail>
        <Detail label="Email">{request.email ?? "None"}</Detail>
        <Detail label="Members">{request.memberCount ? memberCountLabel(request.memberCount) : "Not given"}</Detail>
        <Detail label="Website">
          {request.websiteUrl ? (
            <a className="text-accent-strong underline" href={request.websiteUrl} target="_blank" rel="noreferrer noopener">
              {request.websiteUrl.replace(/^https?:\/\//, "")}
            </a>
          ) : (
            "None"
          )}
        </Detail>
        <Detail label="Instagram">
          {request.instagram ? (
            <a className="text-accent-strong underline" href={`https://instagram.com/${request.instagram}`} target="_blank" rel="noreferrer noopener">
              @{request.instagram}
            </a>
          ) : (
            "None"
          )}
        </Detail>
        <Detail label="Tools today">{request.currentTools ?? "Not given"}</Detail>
      </dl>
    </Card>
  );
}

/** Let a person in who sent no form: a pilot met in person, a friend of a customer. */
function AllowByContact() {
  const allow = useAllowSignUp();
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const body = { ...(phone.trim() ? { phone: phone.trim() } : {}), ...(email.trim() ? { email: email.trim() } : {}) };
    if (!body.phone && !body.email) {
      showToast("Type a mobile number or an email.", { tone: "info" });
      return;
    }
    allow.mutate(body, {
      onSuccess: () => {
        setPhone("");
        setEmail("");
        showToast("This person can sign up now. Send them the message.");
      },
      onError: (error) => showToast(errorText(error, "Could not allow sign-up. Try again."), { tone: "info" }),
    });
  };

  return (
    <Card className="px-4 py-4">
      <h2 className="text-[15px] font-semibold text-ink">Allow a person with no form</h2>
      <form className="mt-3 grid gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end" onSubmit={submit}>
        <Field label="Mobile number" type="tel" autoComplete="off" value={phone} onChange={(event) => setPhone(event.target.value)} placeholder="+971 50 123 4567" />
        <Field label="Email" type="email" autoComplete="off" value={email} onChange={(event) => setEmail(event.target.value)} />
        <Button type="submit" loading={allow.isPending}>
          Allow sign-up
        </Button>
      </form>
    </Card>
  );
}

function OtherAllowanceRow({ allowance }: { allowance: AdminSignUpAllowance }) {
  const label = allowance.phoneE164 ? formatPhone(allowance.phoneE164) : (allowance.email ?? "");
  return (
    <li className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0">
        <p className="font-semibold text-ink">
          {[allowance.phoneE164 ? formatPhone(allowance.phoneE164) : null, allowance.email].filter(Boolean).join(", ")}
        </p>
        <p className="text-[12px] text-ink-3">
          Allowed by {allowance.allowedByStaffEmail}, {formatDateTime(allowance.createdAt)}.
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <AllowanceStatus allowance={allowance} />
        <TakeBackButton target={{ allowanceId: allowance.id }} label={label} />
      </div>
    </li>
  );
}

export function EarlyAccessScreen() {
  const data = useEarlyAccess();

  if (!data.data) {
    return (
      <>
        <PageHeader title="Early access" />
        {data.isLoading ? <SkeletonList /> : <ErrorState error={data.error} onRetry={() => void data.refetch()} />}
      </>
    );
  }

  const { signUpOpen, requests, otherAllowances } = data.data;

  return (
    <>
      <PageHeader
        title="Early access"
        description="The forms from the website. Allow a business, and its owner can sign up."
      />
      <div className="mb-5 flex flex-col gap-3">
        {signUpOpen ? (
          <Callout title="Sign-up is open to all">
            Any person can create a business now, so the allow list has no effect. To make sign-up early access
            only, turn off "Owner sign-up" in <Link to="/settings">Settings</Link>.
          </Callout>
        ) : (
          <Callout title="How to let a business in">
            Press "Allow sign-up" on the form. Then send the owner this message on WhatsApp ("Copy WhatsApp
            message"): "{LET_IN_MESSAGE}" They must sign in with a mobile number or an email that is on the list.
          </Callout>
        )}
        <AllowByContact />
      </div>

      {otherAllowances.length > 0 ? (
        <Card className="mb-5">
          <h2 className="border-b border-line px-4 py-3 text-[15px] font-semibold text-ink">Allowed with no form</h2>
          <ul className="divide-y divide-line">
            {otherAllowances.map((allowance) => (
              <OtherAllowanceRow key={allowance.id} allowance={allowance} />
            ))}
          </ul>
        </Card>
      ) : null}

      {requests.length === 0 ? (
        <EmptyState title="No forms yet" message="Forms from the website show here, newest first." />
      ) : (
        <div className="flex flex-col gap-4">
          {requests.map((request) => (
            <RequestCard key={request.id} request={request} />
          ))}
        </div>
      )}
    </>
  );
}
